import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, XIcon } from 'lucide-react';
import {
  Button,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  SegmentedControl,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import {
  CADENCE_PRESETS,
  DEFAULT_TIMEZONE,
  TIMEZONE_OPTIONS,
  WEEKDAY_LABELS,
  cronToPreset,
  presetToCron,
  type CadencePreset,
  type ScheduleRecord,
  type SkillsResponse,
} from '@/pages/assistant/shared';

/** One independent "when" row: cadence, day, time and timezone. */
interface WhenRow {
  readonly preset: CadencePreset;
  readonly time: string;
  readonly weekday: number;
  readonly customCron: string;
  readonly timezone: string;
}

const NEW_ROW: WhenRow = {
  preset: CADENCE_PRESETS.daily,
  time: '08:00',
  weekday: 1,
  customCron: '0 8 * * *',
  timezone: DEFAULT_TIMEZONE,
};

const MAX_ROWS = 4;

/**
 * Create or edit a schedule: pick a skill, a cadence (preset builder that
 * compiles to cron, with an advanced raw-cron option), timezone and run
 * parameters. The server validates cadence (hourly minimum) and versions.
 */

interface FormState {
  readonly name: string;
  readonly skillKey: string; // `${scope}:${name}`
  readonly rows: readonly WhenRow[];
  readonly parameters: string;
  readonly enabled: boolean;
}

const FIELD =
  'w-full rounded-md border border-admin-line px-2 py-1.5 text-portal-small focus:border-brand/60 focus:outline-none';
const LABEL = 'mb-1 block text-portal-micro font-medium text-portal-muted';

export function ScheduleEditPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scheduleId = useParams<{ scheduleId?: string }>().scheduleId;
  const creating = scheduleId === undefined;

  const skills = useQuery({
    queryKey: ['assistant-skills'],
    queryFn: ({ signal }) => getJson<SkillsResponse>('/assistant/skills', undefined, signal),
  });
  const existing = useQuery({
    queryKey: ['assistant-schedules'],
    queryFn: ({ signal }) => getJson<ScheduleRecord[]>('/assistant/schedules', undefined, signal),
    enabled: !creating,
  });
  const schedule = creating
    ? undefined
    : existing.data?.find((entry) => entry.id === scheduleId);

  const [form, setForm] = useState<FormState | null>(
    creating
      ? { name: '', skillKey: '', rows: [NEW_ROW], parameters: '', enabled: true }
      : null,
  );
  const [notice, setNotice] = useState<string | null>(null);

  // Seed the form once the schedule loads.
  useEffect(() => {
    if (creating || schedule === undefined || form !== null) return;
    setForm({
      name: schedule.name,
      skillKey: `${schedule.skillScope}:${schedule.skillName}`,
      rows: schedule.runTimes.map((runTime) => ({
        ...cronToPreset(runTime.cron),
        customCron: runTime.cron,
        timezone: runTime.timezone,
      })),
      parameters: schedule.parameters,
      enabled: schedule.enabled,
    });
  }, [creating, schedule, form]);

  const save = useMutation({
    mutationFn: (state: FormState) => {
      const [skillScope, skillName] = state.skillKey.split(':');
      const body = {
        name: state.name,
        skillName,
        skillScope,
        runTimes: state.rows.map((row) => ({
          cron: presetToCron(row.preset, row.time, row.weekday, row.customCron),
          timezone: row.timezone,
        })),
        parameters: state.parameters,
        enabled: state.enabled,
        ...(creating ? {} : { expectedVersion: schedule?.version }),
      };
      return creating
        ? sendJson('post', '/assistant/schedules', body)
        : sendJson('put', `/assistant/schedules/${scheduleId}`, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-schedules'] });
      navigate(creating ? '/assistant/schedules' : `/assistant/schedules/${scheduleId}`);
    },
    onError: (error) => setNotice(errorMessage(error)),
  });

  if (!creating && existing.isPending) return <LoadingState label="Loading schedule…" />;
  if (!creating && !existing.isPending && schedule === undefined) {
    return <ErrorNotice title="Schedule not found" message="It may have been deleted." />;
  }
  if (form === null) return <LoadingState label="Loading schedule…" />;

  const canSave = form.name.trim().length >= 2 && form.skillKey !== '' && form.rows.length > 0;

  const setRow = (index: number, patch: Partial<WhenRow>): void =>
    setForm({
      ...form,
      rows: form.rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="AI ASSISTANT"
        title={creating ? 'New schedule' : `Editing ${form.name}`}
        subtitle="The skill defines the process; the schedule defines when it runs and with what parameters."
      />
      <Panel className="p-5">
        {notice === null ? null : (
          <div className="mb-3">
            <ErrorNotice inline title="Could not save" message={notice} />
          </div>
        )}
        <div className="space-y-4">
          <label className="block">
            <span className={LABEL}>Name</span>
            <input
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="Morning shipment check"
              maxLength={80}
              className={FIELD}
            />
          </label>
          <label className="block">
            <span className={LABEL}>Skill to run</span>
            <select
              value={form.skillKey}
              onChange={(event) => setForm({ ...form, skillKey: event.target.value })}
              className={FIELD}
            >
              <option value="">Choose a skill…</option>
              {(skills.data?.skills ?? []).map((skill) => (
                <option key={`${skill.scope}:${skill.name}`} value={`${skill.scope}:${skill.name}`}>
                  {skill.name}
                  {skill.scope === 'global' ? ' (organization)' : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="space-y-2">
            <span className={LABEL}>When it runs</span>
            {form.rows.map((row, index) => (
              <div key={index} className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className={LABEL}>Runs</span>
                  <select
                    value={row.preset}
                    onChange={(event) =>
                      setRow(index, { preset: event.target.value as CadencePreset })
                    }
                    className={FIELD}
                  >
                    <option value={CADENCE_PRESETS.daily}>Every day at…</option>
                    <option value={CADENCE_PRESETS.weekdays}>Weekdays at…</option>
                    <option value={CADENCE_PRESETS.weekly}>Weekly on…</option>
                    <option value={CADENCE_PRESETS.custom}>Custom (cron)</option>
                  </select>
                </label>
                {row.preset === CADENCE_PRESETS.weekly ? (
                  <label className="block">
                    <span className={LABEL}>Day</span>
                    <select
                      value={row.weekday}
                      onChange={(event) => setRow(index, { weekday: Number(event.target.value) })}
                      className={FIELD}
                    >
                      {WEEKDAY_LABELS.map((label, day) => (
                        <option key={label} value={day}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {row.preset === CADENCE_PRESETS.custom ? (
                  <label className="block">
                    <span className={LABEL}>Cron expression</span>
                    <input
                      value={row.customCron}
                      onChange={(event) => setRow(index, { customCron: event.target.value })}
                      placeholder="0 4 * * *"
                      aria-label={`Cron ${index + 1}`}
                      className={`${FIELD} font-mono`}
                    />
                  </label>
                ) : (
                  <label className="block">
                    <span className={LABEL}>Time</span>
                    <input
                      type="time"
                      aria-label={`Time ${index + 1}`}
                      value={row.time}
                      onChange={(event) => setRow(index, { time: event.target.value })}
                      className={FIELD}
                    />
                  </label>
                )}
                <label className="block">
                  <span className={LABEL}>Timezone</span>
                  <select
                    value={row.timezone}
                    aria-label={`Timezone ${index + 1}`}
                    onChange={(event) => setRow(index, { timezone: event.target.value })}
                    className={FIELD}
                  >
                    {TIMEZONE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                    {TIMEZONE_OPTIONS.some((option) => option.value === row.timezone) ? null : (
                      <option value={row.timezone}>{row.timezone}</option>
                    )}
                  </select>
                </label>
                {form.rows.length > 1 ? (
                  <button
                    type="button"
                    aria-label={`Remove schedule ${index + 1}`}
                    onClick={() =>
                      setForm({ ...form, rows: form.rows.filter((_, i) => i !== index) })
                    }
                    className="mb-1 grid size-7 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
                  >
                    <XIcon aria-hidden className="size-4" />
                  </button>
                ) : null}
              </div>
            ))}
            {form.rows.length < MAX_ROWS ? (
              <button
                type="button"
                onClick={() => setForm({ ...form, rows: [...form.rows, NEW_ROW] })}
                className="flex cursor-pointer items-center gap-1 rounded-portal-pill border border-portal-line px-2.5 py-1 text-portal-micro font-bold text-portal-muted hover:border-brand hover:text-brand"
              >
                <PlusIcon aria-hidden className="size-3" /> Add schedule
              </button>
            ) : null}
          </div>
          <label className="block">
            <span className={LABEL}>Run parameters (optional — sent to the skill each run)</span>
            <textarea
              value={form.parameters}
              onChange={(event) => setForm({ ...form, parameters: event.target.value })}
              placeholder="Check events in the next 7 days; only report problems."
              maxLength={2000}
              className={`${FIELD} field-sizing-content min-h-20 resize-y`}
            />
          </label>
          <div className="w-fit">
            <span className={LABEL}>Status</span>
            <SegmentedControl
              label="Schedule status"
              options={[
                { key: 'enabled', label: 'Enabled' },
                { key: 'disabled', label: 'Disabled' },
              ]}
              value={form.enabled ? 'enabled' : 'disabled'}
              onChange={(next) => setForm({ ...form, enabled: next === 'enabled' })}
            />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              navigate(creating ? '/assistant/schedules' : `/assistant/schedules/${scheduleId}`)
            }
          >
            Cancel
          </Button>
          <Button disabled={!canSave || save.isPending} onClick={() => save.mutate(form)}>
            {save.isPending ? 'Saving…' : 'Save schedule'}
          </Button>
        </div>
      </Panel>

    </section>
  );
}
